import axios from 'axios';
import { get, merge, snakeCase } from 'lodash';
import mitt, { Emitter } from 'mitt';
import Alert from './alert';

export class ConnectivityError extends Error {
  innerError: any;

  constructor(err: any) {
    super('Connectivity Error');
    this.innerError = err;
  }
}

class Http {
  headers = {};

  eventEmitter: Emitter;

  constructor() {
    this.eventEmitter = mitt();
  }

  on = (...args: any) => {
    this.eventEmitter.on.apply(args);
  };

  baseUrl: any;

  /**
   * Set the base url. If url ends with a forward slash, trim off.
   */
  setBaseUrl = (url = '') => {
    this.baseUrl = url.replace(/\/$/g, '');
  };

  /**
   * Set default headers to be sent along with every request.
   */
  setHeaders = (headers = {}) => {
    this.headers = {
      ...this.headers,
      ...headers,
    };
  };

  /**
   * Build a fully formed URL. If path starts with http protocol, just use path.
   * Otherwise, join baseUrl & path.
   */
  buildUrl = (path: any) => {
    if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
      return path;
    }

    return this.baseUrl + path;
  };

  request = (method: any, path: any, options: any = {}) => {
    const { hideErrorAlert, hideSuccessAlert, ...rest } = options;

    const axiosOptions = merge(
      {
        method,
        url: this.buildUrl(path),
        headers: this.headers,
      },
      rest
    );

    return axios(axiosOptions)
      .then((res) => {
        const serverSuccessMessage = get(res, 'data.message');
        if (serverSuccessMessage && !hideSuccessAlert) {
          Alert.success(serverSuccessMessage);
        }
        return res;
      })
      .catch((err) => {
        const code = get(err, 'response.statusText');
        this.eventEmitter.emit(snakeCase(code));
        const serverErrorMessage = get(err, 'response.data.message');
        if (serverErrorMessage && !hideErrorAlert) {
          Alert.error(serverErrorMessage);
        }

        if (
          err.response ||
          (err.message && err.message !== 'Network Error' && !err.message.match(/^timeout of \d+ms exceeded$/g))
        ) {
          throw err;
        } else {
          // connectivity errors don't have a response. these are replaced with
          // a sentinel class so we can ignore them in Sentry.
          throw new ConnectivityError(err);
        }
      });
  };

  post = (url: any, data: any, config = {}) => this.request('post', url, { ...config, data });

  get = (url: any, config: any = undefined) => this.request('get', url, config);

  put = (url: any, data: any, config = {}) => this.request('put', url, { ...config, data });

  patch = (url: any, data: any, config = {}) => this.request('patch', url, { ...config, data });

  delete = (url: any, config: any) => this.request('delete', url, config);
}

export default new Http();
